import { useState, useEffect } from "react";
import { getSubmissions } from "../services/submissions";
import { getTenantConsents } from "../services/consents";
import Pagination from "../components/utils/Pagination";
import SubmitRequestModal from "../components/utils/SubmitRequestModal";
import RegisterComplaintModal from "../components/utils/RegisterComplaintModal";
import RequestDetailsPanel from "../components/utils/RequestDetailsPanel";
import { FilterDropdown } from "../components/utils/FilterDropdown";
import { FilterGroup } from "../components/utils/FilterGroup";
import FilterSheet from "../components/utils/FilterSheet";
import RequestCard from "../components/utils/RequestCard";
import { SubmissionStatusBadge, SubmissionTypeBadge } from "../components/utils/SubmissionBadges";
import { formatDate, formatDueIn } from "../utils/formatDate";
import { Button } from "../components/utils/Button";
import { Table } from "../components/utils/Table";

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "request", label: "Data Request" },
  { value: "complaint", label: "Complaint" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "resolved", label: "Resolved" },
  { value: "submitted", label: "Submitted" },
];

const getOrFetchTenantId = async () => {
  const stored = localStorage.getItem("tenant_id");
  if (stored) return stored;
  const res = await getTenantConsents();
  const first = res.data.data?.items?.[0];
  const id = first?.tenant_id || first?.id || "";
  if (id) localStorage.setItem("tenant_id", id);
  return id;
};

const columns = [
  {
    key: "request_id",
    label: "Request ID",
    className: "font-semibold text-gray-800",
    render: (row) => row.request_id || row.id,
  },
  {
    key: "submission_type",
    label: "Type",
    render: (row) => <SubmissionTypeBadge type={row.submission_type} />,
  },
  {
    key: "created_at",
    label: "Raised On",
    sortable: true,
    className: "text-gray-600",
    render: (row) => formatDate(row.created_at),
  },
  {
    key: "status",
    label: "Status",
    render: (row) => <SubmissionStatusBadge status={row.status} />,
  },
  {
    key: "due_in",
    label: "Response Due In",
    sortable: true,
    className: "text-gray-600",
    render: (row) =>
      (row.status || "").toLowerCase() === "resolved" ? (
        "-"
      ) : (
        <span
          className={
            /overdue/i.test(row.due_in || "")
              ? "text-[#C73838] font-medium"
              : undefined
          }
        >
          {formatDueIn(row.due_in) || "-"}
        </span>
      ),
  },
];

export default function MyComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [sharedWithFilter, setSharedWithFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortCol, setSortCol] = useState("created_at");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState("");
  const [companyName, setCompanyName] = useState("Company");
  const [showRequest, setShowRequest] = useState(false);
  const [showComplaint, setShowComplaint] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const pageSize = 10;

  const handleSort = (key) => {
    setSortOrder((o) => (sortCol === key ? (o === "asc" ? "desc" : "asc") : "desc"));
    setSortCol(key);
    setPage(1);
  };

  useEffect(() => {
    getTenantConsents()
      .then((res) => setCompanies(res.data.data?.items || []))
      .catch(() => setCompanies([]));
  }, []);

  const fetchComplaints = () => {
    setLoading(true);
    (sharedWithFilter
      ? Promise.resolve(sharedWithFilter)
      : getOrFetchTenantId()
    )
      .then((id) => {
        setTenantId(id);
        return getSubmissions(id, {
          submission_type: typeFilter || undefined,
          status: statusFilter || undefined,
          search,
          page,
          page_size: pageSize,
          sort_by: "created_at",
          sort_order: sortOrder,
        });
      })
      .then((res) => {
        let items = res.data.data?.items || [];
        // Defensive client-side filter — the API doesn't reliably honor
        // the submission_type/status query params, so re-filter what it returns.
        if (typeFilter) {
          items = items.filter(
            (i) =>
              (i.submission_type || "").toLowerCase() ===
              typeFilter.toLowerCase(),
          );
        }
        if (statusFilter) {
          items = items.filter(
            (i) =>
              (i.status || "").toLowerCase() === statusFilter.toLowerCase(),
          );
        }
        setComplaints(items);
        setTotal(
          typeFilter || statusFilter ? items.length : res.data.data?.total || 0,
        );
        setCompanyName(res.data.data?.items?.[0]?.tenant_name || "Company");
      })
      .catch(() => {
        setComplaints([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchComplaints, [
    search,
    sharedWithFilter,
    typeFilter,
    statusFilter,
    sortOrder,
    page,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-4 md:p-6 pb-24 md:pb-6">
      {/* Desktop toolbar */}
      <div className="hidden md:flex items-center gap-3 mb-5">
        <div className="relative max-w-xs flex-1">
          <img
            src="/assets/search.svg"
            alt=""
            width={16}
            height={16}
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <FilterGroup
          filters={[typeFilter, statusFilter]}
          onClear={() => {
            setTypeFilter("");
            setStatusFilter("");
            setPage(1);
          }}
        >
          <FilterDropdown
            label="Type"
            value={typeFilter}
            onChange={(value) => {
              setTypeFilter(value);
              setPage(1);
            }}
            options={[
              { value: "request", label: "Update Request" },
              { value: "erasure", label: "Erasure Request" },
              { value: "complaint", label: "Complaint" },
            ]}
          />
        </FilterGroup>
        <div className="ml-auto flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowComplaint(true)}
            className="!border-[#C73838] !text-[#C73838] hover:!bg-red-50"
          >
            Register a Complaint
          </Button>
          <Button
            onClick={() => setShowRequest(true)}
            style={{ background: "#2d7a7a" }}
          >
            Submit a Request
          </Button>
        </div>
      </div>

      {/* Mobile toolbar */}
      <div className="flex md:hidden items-center gap-2 mb-4">
        <div className="relative flex-1">
          <img
            src="/assets/search.svg"
            alt=""
            width={16}
            height={16}
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search Requests"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <Button
          variant="tertiary"
          onClick={() => setShowFilterSheet(true)}
          className="!p-2"
          aria-label="Filter"
        >
          <img src="/assets/filter.svg" alt="" width={18} height={18} />
        </Button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table
          columns={columns}
          rows={complaints}
          rowKey={(row, idx) => row.id || idx}
          onRowClick={(row) => setSelectedRequest(row)}
          loading={loading}
          loadingText="Loading..."
          emptyText="No requests or complaints found."
          sortCol={sortCol}
          sortDir={sortOrder}
          onSort={handleSort}
        />
      </div>

      {/* Mobile card list */}
      <div className="flex flex-col gap-3 md:hidden">
        {loading ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            Loading...
          </div>
        ) : complaints.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            No requests or complaints found.
          </div>
        ) : (
          complaints.map((c, idx) => (
            <RequestCard
              key={c.id || idx}
              request={c}
              onClick={() => setSelectedRequest(c)}
            />
          ))
        )}
      </div>

      <div className="hidden md:block">
        <Pagination
          page={page}
          total={total}
          pageSize={pageSize}
          onChange={setPage}
        />
      </div>

      {/* Mobile sticky bottom actions */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 flex gap-3 p-4">
        <Button
          variant="outline"
          onClick={() => setShowComplaint(true)}
          className="flex-1 !py-3 !border-[#C73838] !text-[#C73838] hover:!bg-red-50"
        >
          Register a Complaint
        </Button>
        <Button
          onClick={() => setShowRequest(true)}
          className="flex-1 !py-3"
          style={{ background: "#2d7a7a" }}
        >
          Submit a Request
        </Button>
      </div>

      {showRequest && (
        <SubmitRequestModal
          tenantId={tenantId}
          onClose={() => setShowRequest(false)}
          onSuccess={fetchComplaints}
        />
      )}
      {showComplaint && (
        <RegisterComplaintModal
          tenantId={tenantId}
          companyName={companyName}
          onClose={() => setShowComplaint(false)}
          onSuccess={fetchComplaints}
        />
      )}
      {selectedRequest && (
        <RequestDetailsPanel
          request={selectedRequest}
          tenantId={tenantId}
          onClose={() => setSelectedRequest(null)}
        />
      )}
      {showFilterSheet && (
        <FilterSheet
          title="Filter Requests & Complaints"
          typeOptions={TYPE_FILTER_OPTIONS}
          statusOptions={STATUS_FILTER_OPTIONS}
          typeValue={typeFilter}
          statusValue={statusFilter}
          onApply={(type, status) => {
            setTypeFilter(type);
            setStatusFilter(status);
            setPage(1);
            setShowFilterSheet(false);
          }}
          onClose={() => setShowFilterSheet(false)}
        />
      )}
    </div>
  );
}
